import { createRef, Component, RefObject } from 'react';
import styled from 'styled-components';

import { IConvertedImages } from '../../../../../common/store/interface/IConvertedImages';
import LabelIcon from '../modules/LabelIcon';
import NewsFeedPrice from '../modules/NewsFeedPrice';
import Thumbnail, { ImageSize } from '../modules/Thumbnail';
import ViewableElement from '../modules/ViewableElement';
import NewsFeedCommentCount from '../modules/NewsFeedCommentCount';
import { ViewableElementWrapper } from '../modules/ViewableElementWrapper';
import { FetchStatus } from '../../../store/personalizedFeed';
import { ExpertRecommendTimelineLabel } from '../../../../article/articleDetail/components/js/ExpertRecommendLabel';
import { NewsFeedItemText } from '../newsFeed/NewsFeedItemText';
import { NewsFeedItemTitle } from '../newsFeed/NewsFeedItemTitle';
import { NewsFeedItemLink } from '../newsFeed/NewsFeedItemLink';
import NewsFeedItemThumbnail from '../newsFeed/NewsFeedItemThumbnail';
import NewsFeedItem from '../newsFeed/NewsFeedItem';
import FeedArticleComment from './FeedArticleComment';
import FeedArticleDislike from './FeedArticleDislike';
import { FeedItemSub } from './FeedItemSub';
import { isDisplayExpertRecommendLabel } from '@/public/template/common/expert/hooks/displayExpertRecommendHooks';

/**
 * DISLIKEに登録完了されてからリロードされるまでの非表示対応用
 *
 * @param root0
 * @param root0.$isDislike
 */
const FeedWrap = styled.div<{ $isDislike: boolean }>`
  ${({ $isDislike }) => ($isDislike ? 'display: none;' : '')}
`;

interface IProps {
  title?: string;
  link?: string;
  mediaName?: string;
  thumbUrl?: string;
  resizedThumbUrl: IConvertedImages | null;
  video?: {
    contentId?: string;
    duration?: string;
  };
  createTime?: {
    date: string;
    time: string;
  };
  createUnixTimeStamp: number;
  isShowDateTime: boolean;
  dataClParams: string;
  viewableDataClParams: string;
  dataUal: string;
  isPay: boolean;
  taxIncludedPrice?: number;
  isCommentModule: boolean;
  isAuthorCommentModule: boolean;
  authorName?: string;
  authorThumbUrl?: string;
  authorTitle?: string;
  commentText?: string;
  isShowComment: boolean;
  articleViewablePos: number;
  commentViewablePos?: number | null;
  contentId?: string;
  dislike?: {
    isSetting: boolean;
  };
  addDislikeForChild: () => void | null;
  removeDislikeForChild: () => void | null;
  isShowDislikeModule: boolean;
  dislikeModulePos: number | null;
  showCommentCount: boolean;
  fetchArticleAdditionalData?: () => void;
  setIsOpenDislikePopUp: (contentId: string, isOpenDislikePopUp: boolean) => void;
  isOpenDislikePopUp: boolean;
  commentCountFetchStatus?: FetchStatus;
  commentCount?: number;
  isFetchedExpertRecommendArticles: boolean;
  expertRecommendCount?: number;
  isNewsFeedItemRelative: boolean;
}

const LabelIconWrapper = styled.span`
  margin-left: 12px;
  font-size: 0; /* 縦を揃える */
`;

const ExpertRecommendLabel = styled.div`
  margin-left: 8px;
`;

const NewsFeedItemSourceWrap = styled.div`
  display: flex;
  align-items: center;
  margin-left: -8px; /* 子要素を並べた時に要素の左にマージンをつけるため、先頭のマージンをマイナスマージンで相殺 */
`;

const StyledNewsFeedItem = styled(NewsFeedItem)<{ $isNewsFeedItemRelative: boolean }>`
  /* DISLIKE用にabsoluteが指定できるようnewsFeed_itemをrelativeで上書き設定する */
  ${({ $isNewsFeedItemRelative }) => ($isNewsFeedItemRelative ? 'position: relative;' : '')}
`;

const NewsFeedItemMedia = styled.span`
  margin-left: 8px;
`;

const NewsFeedItemDate = styled.time`
  margin-left: 8px;
`;

const NewsFeedItemSub = styled(FeedItemSub)<{ $isNewsFeedItemRelative: boolean }>`
  /* DISLIKE用に媒体名の表示列の右側に余白を持たせる設定を追加 */
  ${({ $isNewsFeedItemRelative }) => ($isNewsFeedItemRelative ? 'padding-right: 20px;' : '')}
`;

export default class FeedArticleItem extends Component<IProps> {
  private liElement: RefObject<HTMLLIElement>;

  constructor(props: IProps) {
    super(props);
    this.liElement = createRef<HTMLLIElement>();
  }

  /**
   *
   */
  public componentDidMount() {
    // コメント件数取得用のイベントリスナーを追加する
    if (
      this.props.showCommentCount &&
      this.liElement.current &&
      this.props.commentCount === undefined &&
      this.props.expertRecommendCount === undefined
    ) {
      window.addEventListener('scroll', this.fetchIfAdditionalDataViewIn, { passive: true });
    }
  }

  /**
   *
   */
  public render() {
    const videoDuration: string | undefined = this.props.video?.duration;
    // コメント非表示設定したユーザには、 棚出しのコメントを出さない
    const commentClass = this.props.isCommentModule && this.props.isShowComment ? 'newsFeed_item-comment' : '';
    // オーサーコメント出力分岐
    const isAuthorCommentModule =
      this.props.isAuthorCommentModule !== undefined ? this.props.isAuthorCommentModule : false;

    const NewsFeedComment =
      this.props.isCommentModule && this.props.isShowComment && this.props.commentViewablePos ? (
        <FeedArticleComment
          isAuthorCommentModule={isAuthorCommentModule}
          authorName={this.props.authorName}
          authorThumbUrl={this.props.authorThumbUrl}
          authorTitle={this.props.authorTitle}
          commentText={this.props.commentText}
          commentViewablePos={this.props.commentViewablePos}
        />
      ) : null;

    const dateTime =
      this.props.isShowDateTime && this.props.createTime ? (
        <NewsFeedItemDate>{`${this.props.createTime.date} ${this.props.createTime.time}`}</NewsFeedItemDate>
      ) : null;

    const pay = this.props.isPay ? (
      <LabelIconWrapper>
        <LabelIcon type="PAY" />
      </LabelIconWrapper>
    ) : null;

    // DISLIKEに登録完了された表示が出ているFeedかどうか
    const isDislike = this.props.dislike?.isSetting === true;

    // DISLIKEの表示対象のみモジュールを表示する
    const NewsFeedDislike =
      this.props.isShowDislikeModule && this.props.contentId ? (
        <FeedArticleDislike
          contentId={this.props.contentId}
          isSetting={isDislike}
          addDislikeForGrandchild={() => this.props.addDislikeForChild()}
          removeDislikeForGrandchild={() => this.props.removeDislikeForChild()}
          articlePos={this.props.articleViewablePos}
          commentPos={NewsFeedComment !== null && this.props.commentViewablePos ? this.props.commentViewablePos : null}
          dislikePos={this.props.dislikeModulePos}
          setIsOpenDislikePopUp={this.props.setIsOpenDislikePopUp}
          isOpenDislikePopUp={this.props.isOpenDislikePopUp}
        />
      ) : null;

    const ArticleAdditionalDataElement = ({}) => {
      if (this.props.commentCountFetchStatus !== 'complete' || !this.props.isFetchedExpertRecommendArticles) {
        return null;
      }

      // 表示対象時間かつエキスパートレコメンドがある場合は表示
      if (
        isDisplayExpertRecommendLabel(new Date(this.props.createUnixTimeStamp)) &&
        this.props.expertRecommendCount &&
        this.props.expertRecommendCount > 0
      ) {
        return (
          <ExpertRecommendLabel>
            <ExpertRecommendTimelineLabel />
          </ExpertRecommendLabel>
        );
      }

      if (this.props.showCommentCount && this.props.commentCount && this.props.commentCount > 0) {
        return <NewsFeedCommentCount commentCount={this.props.commentCount} />;
      }
      return null;
    };

    return (
      <StyledNewsFeedItem
        className={commentClass}
        data-ual-view-type="list"
        data-ual={this.props.dataUal}
        ref={this.liElement}
        $isNewsFeedItemRelative={this.props.isNewsFeedItemRelative}
      >
        <FeedWrap $isDislike={isDislike}>
          <NewsFeedItemLink href={this.props.link} data-cl-params={this.props.dataClParams} data-ual-gotocontent>
            <ViewableElementWrapper>
              <NewsFeedItemThumbnail>
                <Thumbnail
                  imageSize={ImageSize.MIDDLE}
                  imageSrc={this.props.thumbUrl}
                  resizedImageSrc={this.props.resizedThumbUrl}
                  hasVideo={!!videoDuration}
                  videoDuration={videoDuration}
                />
              </NewsFeedItemThumbnail>
              <ViewableElement
                id={`viewable_newsFeed_bottom_${this.props.articleViewablePos}`}
                position="bottom"
                dataClParams={this.props.viewableDataClParams}
              />
            </ViewableElementWrapper>
            <NewsFeedItemText>
              <NewsFeedItemTitle>{this.props.title}</NewsFeedItemTitle>
              <NewsFeedItemSub $isNewsFeedItemRelative={this.props.isNewsFeedItemRelative}>
                <NewsFeedItemSourceWrap>
                  <ArticleAdditionalDataElement />
                  <NewsFeedItemMedia>{this.props.mediaName}</NewsFeedItemMedia>
                  {dateTime}
                  {pay}
                </NewsFeedItemSourceWrap>
                {this.props.taxIncludedPrice ? <NewsFeedPrice taxIncludedPrice={this.props.taxIncludedPrice} /> : null}
              </NewsFeedItemSub>
            </NewsFeedItemText>
            {/* コメント棚だしモジュール */}
            {NewsFeedComment}
          </NewsFeedItemLink>
        </FeedWrap>
        {/* DISLIKEモジュール */}
        {NewsFeedDislike}
      </StyledNewsFeedItem>
    );
  }

  private fetchIfAdditionalDataViewIn = () => {
    // 取得中や必要な要素がない場合は処理をしない
    if (
      this.props.commentCountFetchStatus === 'fetching' ||
      !this.props.isFetchedExpertRecommendArticles ||
      this.liElement.current === null ||
      this.props.fetchArticleAdditionalData === undefined
    ) {
      return;
    }

    // 画面内(-200px上のバッファ有り)に入ったらfetchイベントを呼び出す
    if (
      this.liElement.current.getBoundingClientRect().top - 200 - window.innerHeight < 0 &&
      this.liElement.current.getBoundingClientRect().bottom + 200 > 0
    ) {
      // イベントリスナーを削除して取得処理を実行
      window.removeEventListener('scroll', this.fetchIfAdditionalDataViewIn);
      this.props.fetchArticleAdditionalData();
    }
  };
}
